<template>
  <div class="modal-content">
    <div class="modal-header">
      <h4 class="modal-title">{{ _('Add to user interface') }}</h4>
    </div>
    <div class="modal-body">
      <p v-if="loading" class="text-muted"><i class="fa fa-fw fa-cog fa-spin"></i> {{ _('Loading...') }}</p>
      <p v-else-if="loadFailed" class="text-danger">{{ _('The user interfaces could not be loaded.') }}</p>
      <template v-else>
        <p class="help-block">
          {{ isDashboard
            ? _('Add this dashboard to a menu section so people can open it from there.')
            : _('Add this tool to a menu section so people can open it from there.') }}
        </p>
        <div class="form-group form-group-tight">
          <label>{{ _('User interface') }}</label>
          <div v-if="!showClientPicker" class="checkbox current-ui">
            <label>
              <input type="checkbox" checked disabled />
              {{ selectedClientName }}
              <small v-if="selectedClient && selectedClient.current" class="text-muted"> · {{ _('This user interface') }}</small>
            </label>
          </div>
          <div v-else class="client-picker">
            <div v-for="client in destinations" :key="client.id" class="radio">
              <label>
                <input type="radio" :value="client.id" v-model.number="selectedClientId" />
                {{ client.name || ('#' + client.id) }}
                <small v-if="client.current" class="text-muted"> · {{ _('This user interface') }}</small>
                <small v-else-if="client.istemplate" class="text-muted"> · {{ _('Template') }}</small>
              </label>
            </div>
          </div>
          <button v-if="destinations.length > 1" type="button" class="extra-toggle" v-on:click="showClientPicker = !showClientPicker">
            {{ showClientPicker ? _('Cancel') : _('Change UI') }}
          </button>
        </div>
        <div class="form-group">
          <label>{{ _('Where') }}</label>
          <div class="radio">
            <label>
              <input type="radio" value="existing" v-model="mode" :disabled="!currentLayouts.length" />
              {{ _('Add to an existing layout section') }}
            </label>
          </div>
          <div class="radio">
            <label>
              <input type="radio" value="new_section" v-model="mode" />
              {{ _('Create a new layout section') }}
            </label>
          </div>
        </div>
        <div v-if="mode === 'existing'" class="form-group">
          <label id="maxPlaceLayoutLabel">{{ _('Layout section') }}</label>
          <div class="layout-picker" role="listbox" aria-labelledby="maxPlaceLayoutLabel">
            <button
              v-for="layout in currentLayouts"
              :key="layout.id"
              type="button"
              role="option"
              class="layout-pick"
              :class="{ active: layout.id === layoutId }"
              :aria-selected="layout.id === layoutId"
              v-on:click="layoutId = layout.id"
            >
              <i :class="iconClass(layout.icon)" aria-hidden="true"></i>
              <span>{{ layout.name }}</span>
            </button>
          </div>
          <p v-if="selectedLayout && selectedLayout.shared_with > 0" class="help-block">
            {{ _('This layout section is also used by %s other user interfaces. It will appear there too.').replace('%s', selectedLayout.shared_with) }}
          </p>
          <div v-if="selectedLayout && selectedLayout.columns.length > 1" class="form-group" style="margin-top: 12px">
            <label for="maxPlaceColumn">{{ _('Column') }}</label>
            <select id="maxPlaceColumn" class="form-control" v-model="column">
              <option v-for="col in selectedLayout.columns" :key="col.identifier" :value="col.identifier">{{ col.identifier }}</option>
            </select>
          </div>
        </div>
        <div v-else>
          <div class="form-group">
            <label for="maxPlaceName">{{ _('Section name') }}</label>
            <input id="maxPlaceName" type="text" class="form-control" v-model="sectionName" :placeholder="specTitle" />
          </div>
          <div class="form-group">
            <label>{{ _('Icon') }}</label><br />
            <button type="button" class="btn btn-default" v-on:click="selectIconDialog">{{ _('Select icon') }}</button>
            <i v-if="icon" :class="'fa fa-fw fa-2x fa-' + icon"></i>
            <button v-if="icon && icon !== defaultIcon" type="button" class="btn btn-danger" v-on:click="icon = defaultIcon">{{ _('Remove icon') }}</button>
          </div>
        </div>
      </template>
    </div>
    <div class="modal-footer">
      <button type="button" class="btn btn-default" v-on:click="hideModal"><i class="fa fa-fw fa-close"></i> {{ _('Cancel') }}</button>
      <button type="button" class="btn btn-success" :disabled="!canSubmit" v-on:click="submit">
        <i class="fa fa-fw" :class="saving ? 'fa-cog fa-spin' : 'fa-plus'"></i>
        {{ saving ? _('Saving...') : _('Add to user interface') }}
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import errors from '~/lib/errors'
import modals from '~/lib/modals'

export default {
  mixins: [errors, modals],
  props: {
    specId: { type: Number, required: true },
    specTitle: { type: String, default: '' },
    specKind: { type: String, default: 'interface' }
  },
  data () {
    return {
      loading: true,
      loadFailed: false,
      saving: false,
      destinations: [],
      mode: 'new_section',
      layoutId: null,
      column: null,
      sectionName: '',
      icon: '',
      selectedClientId: null,
      showClientPicker: false
    }
  },
  computed: {
    ...mapGetters(['shopId', 'baseDomain', 'apiKey', 'clientIdNumber', 'clientName']),
    isDashboard () {
      return this.specKind === 'dashboard'
    },
    defaultIcon () {
      return this.isDashboard ? 'gauge' : 'screwdriver-wrench'
    },
    selectedClient () {
      return this.destinations.find(client => client.id === this.selectedClientId)
        || this.destinations.find(client => client.current)
        || this.destinations[0]
        || null
    },
    selectedClientName () {
      return this.selectedClient ? (this.selectedClient.name || ('#' + this.selectedClient.id)) : ''
    },
    currentLayouts () {
      return this.selectedClient ? this.selectedClient.layouts : []
    },
    selectedLayout () {
      return this.currentLayouts.find(layout => layout.id === this.layoutId) || null
    },
    canSubmit () {
      if (this.loading || this.loadFailed || this.saving || !this.selectedClient) {
        return false
      }
      if (this.mode === 'existing') {
        return !!this.layoutId
      }
      return true
    }
  },
  watch: {
    selectedClientId (id, previous) {
      this.applyClientLayouts()
      if (previous != null) {
        this.showClientPicker = false
      }
    },
    layoutId () {
      const columns = this.selectedLayout ? this.selectedLayout.columns : []
      this.column = columns.length ? columns[0].identifier : null
    }
  },
  methods: {
    hideModal () {
      this.$emit('close')
    },
    iconClass (icon) {
      return 'fa fa-fw' + (icon ? ' fa-' + icon : '')
    },
    applyClientLayouts () {
      if (this.currentLayouts.length) {
        this.mode = 'existing'
        this.layoutId = this.currentLayouts[0].id
      } else {
        this.mode = 'new_section'
        this.layoutId = null
      }
    },
    selectIconDialog () {
      this.showDialog({
        title: this._('Select icon'),
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'close',
            handler: () => {}
          },
          {
            title: this._('Ok'),
            class: 'success',
            icon: 'check',
            handler: (data) => {
              this.icon = data.icon || this.defaultIcon
            }
          }
        ],
        fields: [
          {
            title: this._('Icon'),
            type: 'icon',
            name: 'icon',
            defaultValue: this.icon
          }
        ]
      })
    },
    async loadDestinations () {
      this.loading = true
      this.loadFailed = false
      try {
        const response = await axios.get(this.baseDomain + this.shopId + '/maxspecs/' + this.specId + '.json?destinations=1&client_id=' + (this.clientIdNumber || 0) + '&apikey=' + this.apiKey)
        this.destinations = Array.isArray(response.data.destinations) ? response.data.destinations : []
        this.sectionName = this.specTitle
        this.icon = this.defaultIcon
        const current = this.destinations.find(client => client.current)
          || this.destinations.find(client => client.id === this.clientIdNumber)
          || this.destinations[0]
        this.selectedClientId = current ? current.id : null
        this.applyClientLayouts()
      } catch (e) {
        this.loadFailed = true
        this.showError(e)
      }
      this.loading = false
    },
    async submit () {
      if (!this.canSubmit) {
        return
      }
      this.saving = true
      const body = {
        id: this.specId,
        action: 'place',
        mode: this.mode,
        client_ids: [this.selectedClient.id]
      }
      if (this.mode === 'existing') {
        body.layout_id = this.layoutId
        if (this.column) {
          body.column = this.column
        }
      } else {
        body.section_name = this.sectionName || this.specTitle
        body.icon = this.icon || this.defaultIcon
      }
      try {
        const response = await axios.patch(this.baseDomain + this.shopId + '/maxspecs/' + this.specId + '.json?apikey=' + this.apiKey, body)
        this.$emit('placed', response.data.placed)
      } catch (e) {
        this.saving = false
        return this.showError(e)
      }
      this.saving = false
    }
  },
  mounted () {
    this.loadDestinations()
  }
}
</script>

<style scoped>
.form-group-tight {
  margin-bottom: 8px;
}
.current-ui {
  margin-top: 0;
  margin-bottom: 0;
}
.current-ui label {
  color: inherit;
  cursor: default;
}
.current-ui input[disabled] {
  opacity: 1;
  cursor: default;
}
.extra-toggle {
  display: inline-flex;
  align-items: center;
  padding: 0;
  margin: 4px 0 0;
  border: 0;
  background: none;
  color: #337ab7;
  font-size: inherit;
  line-height: 1.4;
  cursor: pointer;
}
.extra-toggle:hover,
.extra-toggle:focus {
  text-decoration: underline;
}
.client-picker {
  max-height: 180px;
  overflow-y: auto;
}
.client-picker .radio {
  margin-top: 4px;
  margin-bottom: 4px;
}
.layout-picker {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 240px;
  overflow-y: auto;
  margin-top: 4px;
}
.layout-pick {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  text-align: left;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
}
.layout-pick.active {
  border-color: #337ab7;
  background: #f0f7fc;
}
.layout-pick i {
  width: 1.25em;
  text-align: center;
}
</style>
